/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { Chip, Link } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useGridApiRef, GridActionsCellItem } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import { useFormik } from 'formik';
import * as yup from 'yup';
import AskDialog from '../../components/common/dialog/AskDialog';
import * as empApi from '../../lib/api/employee';
import { useNavigate } from 'react-router-dom';

import { listEmployees } from '../../modules/empModule/employees';
import EmployeeList from '../../components/employeemng/EmployeeList';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import { EmployeeListBox } from '../../components/employeemng/EmployeeListBox';
import EmpOrdList from '../../components/common/form/modal/EmpOrdList';
import { useIsAdmin } from '../../hooks/useIsAdmin';

import _ from 'lodash';

const isAdminRow = (row) =>
  typeof row?.role === 'string' && row.role.toUpperCase().includes('ADMIN');

const EmpListContainer = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  // 등록/수정/삭제/권한변경 버튼 노출 판단 - 관리자만. 실제 차단은 서버(UserController의
  // AdminGuard)가 한다. 보안 배치 2(권한, 2026-10-27).
  const isAdmin = useIsAdmin();
  // 자기 강등 경고(Fable 재검토 항목 5, 2026-10-27) 판단용 - 로그인한 본인의 usrUuid.
  const currentUser = useSelector(({ auth }) => auth.user);
  const [roleTarget, setRoleTarget] = useState(null);

  const [removeId, setRemoveId] = useState(null);
  const [orderName, setOrderName] = useState('');
  const [searchYn, setSearchYn] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('usrNm');
  const [searchWords, setsearchWords] = useState(null);

  const searchList = (values) => {
    //console.log(values, 'values');
    let searchData = {
      orderNm: orderName,
      useYn: !values.searchYn ? 'Y' : 'N',
      [values.searchKeyword]: values.searchWords,
    };

    dispatch(listEmployees(searchData));
    setSearchKeyword(values.searchKeyword);
    setsearchWords(values.searchWords);
    setSearchYn(values.searchYn);
  };

  const { employees, error, loading } = useSelector(
    ({ empModule, loading }) => ({
      employees: empModule.employees.employees,
      error: empModule.employees.error,
      loading: loading['employee/LIST_EMPLOYEES'],
    }),
  );

  const apiRef = useGridApiRef();

  const handleRowEditStart = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const handleRowEditStop = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const handleCellFocusOut = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const handleEditClick = (usrUuid) => (event) => {
    event.stopPropagation();
    navigate(`/employeemng/${usrUuid}/edit`);
  };

  const handleDeleteClick = (id) => (event) => {
    event.stopPropagation();

    //remove 작업
    setVisible(true);
    setRemoveId(id);
  };

  const onSave = async (data) => {
    const empData = _.clone(data);
    empData.useYn = empData.useYn === true ? 'Y' : 'N';

    try {
      const response = await empApi.editEmployee(empData);

      if (response.data.result === 'success') {
        apiRef.current.updateRows([{ ...data, isNew: false }]);
        enqueueSnackbar('수정 성공', {
          variant: 'success',
        });
      }
    } catch (err) {
      //apiRef.current.updateRows([{ id: data.id, _action: 'delete' }]);
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const onRemove = async () => {
    try {
      const res = await empApi.removeEmployee(removeId);

      if (res.data.info) {
        searchList(formik.values);
        enqueueSnackbar('미사용 처리 성공', {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
    setRemoveId(null);
  };

  const handleRoleToggleClick = (row) => (event) => {
    event.stopPropagation();
    setRoleTarget(row);
  };

  const onRoleCancel = () => setRoleTarget(null);

  const onRoleConfirm = async () => {
    if (!roleTarget) return;
    const nextRole = isAdminRow(roleTarget) ? 'USER_ROLE' : 'ADMIN_ROLE';
    try {
      await empApi.changeUsrRole(roleTarget.usrUuid, nextRole);
      enqueueSnackbar(
        nextRole === 'ADMIN_ROLE' ? '관리자로 변경했습니다.' : '일반 사용자로 변경했습니다.',
        { variant: 'success' },
      );
      searchList(formik.values);
    } catch (err) {
      let message =
        typeof err.response !== 'undefined' ? err.response.data.message : err.message;
      enqueueSnackbar(message, { variant: 'error' });
    }
    setRoleTarget(null);
  };

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        hide: true,
        type: 'string',
        editable: false,
        disableExport: true,
      },
      {
        field: 'usrNo',
        headerName: '사번',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: ({ value }) => value,
        renderCell: (params) => (
          <Link
            underline="none"
            component={RouterLink}
            to={`/employeemng/${params.row.usrUuid}`}
          >
            {params.row.usrNo}
          </Link>
        ),
      },
      {
        field: 'dptNm',
        headerName: '부서명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
      },
      {
        field: 'usrNm',
        headerName: '이름',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'gradeNm',
        headerName: '직급',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'telNo',
        headerName: '연락처',
        minWidth: 120,
        flex: 0.6,
        type: 'string',
        // 서버(PUT /api/v1/user/info/{usrUuid})가 관리자만 허용하므로 비관리자는 그리드에서도
        // 인라인 편집을 시작할 수 없게 막는다. 보안 배치 2(권한, 2026-10-27).
        editable: isAdmin,
      },
      {
        field: 'emlAddr',
        headerName: '이메일',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: isAdmin,
      },
      {
        field: 'role',
        headerName: '권한',
        minWidth: 90,
        flex: 0.5,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Chip
            size="small"
            label={isAdminRow(params.row) ? '관리자' : '일반'}
            color={isAdminRow(params.row) ? 'primary' : 'default'}
            variant={isAdminRow(params.row) ? 'filled' : 'outlined'}
          />
        ),
      },
      // {
      //   field: 'gender',
      //   headerName: '성별',
      //   minWidth: 80,
      //   flex: 0.6,
      //   type: 'string',
      //   editable: false,
      //   align: 'center',
      //   headerAlign: 'center',
      //   valueFormatter: ({ value }) => (value === 'M' ? '남성' : '여성'),
      //   renderCell: (params) => (params.value === 'M' ? '남성' : '여성'),
      // },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 140,
        cellClassName: 'actions',
        getActions: ({ id, row }) => {
          // 등록/수정/삭제/권한변경은 서버가 관리자만 허용한다(UserController) - 비관리자는
          // 아이콘 자체를 숨긴다. 실제 차단은 서버가 한다. 보안 배치 2(권한, 2026-10-27).
          if (!isAdmin) {
            return [];
          }
          return [
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(row.usrUuid)}
              color="inherit"
            />,
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={handleDeleteClick(id)}
              color="inherit"
            />,
            <GridActionsCellItem
              icon={isAdminRow(row) ? <PersonIcon /> : <AdminPanelSettingsIcon />}
              label={isAdminRow(row) ? '일반으로 변경' : '관리자로 변경'}
              onClick={handleRoleToggleClick(row)}
              color="inherit"
            />,
          ];
        },
      },
    ],
    [
      apiRef,
      isAdmin,
      handleEditClick,
      handleDeleteClick,
      handleRoleToggleClick,
    ],
  );

  const [visible, setVisible] = useState(false);

  const onCancle = () => {
    setVisible(false);
    setRemoveId(null);
  };

  const onConfirm = () => {
    setVisible(false);
    onRemove();
  };

  const handleAdd = () => {
    navigate('/employeemng/add');
  };

  //검색 관련 사항
  const initialValues = {
    searchYn: searchYn,
    searchKeyword: searchKeyword,
    searchWords: searchWords,
  };

  const validationSchema = yup.object({
    searchYn: yup.boolean(),
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);

      setTimeout(() => {
        searchList(values);
        setSubmitting(false);
      }, 1000);
    },
  });

  useEffect(() => {
    searchList(formik.values);
  }, [orderName]);

  const onChangeOrder = (order) => {
    if (order === orderName) {
      order = '';
    }

    setOrderName(order);
  };

  return (
    <>
      <BreadcrumbsContainer title="환경설정/사원관리/사원 리스트" icon="user" />
      <EmployeeListBox formik={formik} handleAdd={isAdmin ? handleAdd : undefined} />
      <EmpOrdList orderName={orderName} onChangeOrder={onChangeOrder} />
      <EmployeeList
        apiRef={apiRef}
        columns={columns}
        employees={employees}
        error={error}
        loading={loading}
        handleRowEditStart={handleRowEditStart}
        handleRowEditStop={handleRowEditStop}
        handleCellFocusOut={handleCellFocusOut}
          columnVisibilityModel={{
          // Hide columns status and traderName, the other columns will remain visible
          id: false,    
        }} 
      />
      <AskDialog
        visible={visible}
        title="사원 미사용"
        description="선택한 사원을 미사용 처리하시겠습니까?"
        confirmText="미사용"
        onConfirm={onConfirm}
        onCancle={onCancle}
      />
      <AskDialog
        visible={Boolean(roleTarget)}
        title="권한 변경"
        description={
          roleTarget
            ? isAdminRow(roleTarget)
              ? roleTarget.usrUuid === currentUser?.usrUuid
                ? '본인을 일반 사용자로 변경합니다. 변경 즉시 로그아웃되며, 다시 로그인해도 관리자 화면을 쓸 수 없습니다. 계속하시겠습니까?'
                : `${roleTarget.usrNm}님을 일반 사용자로 변경하시겠습니까?`
              : `${roleTarget.usrNm}님을 관리자로 지정하시겠습니까?`
            : ''
        }
        confirmText="변경"
        onConfirm={onRoleConfirm}
        onCancle={onRoleCancel}
      />
    </>
  );
};

export default EmpListContainer;
